import { useState } from "react";
import {
  CopyButton,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, LABEL, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  type ArrheniusStrings,
  arrheniusMessages,
  DEFAULT_SIG,
  ENERGY_OPTIONS,
  eaText,
  R_TEXT,
  SolvedCell,
  TEMPERATURE_OPTIONS,
} from "@/components/tools/chem-physical/arrhenius-shared";
import { FailureNotice } from "@/components/tools/chem-physical/heat-shared";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import { QuantityInput } from "@/components/tools/chemistry/quantity-input";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  solveTwoPoint,
  type TwoPointSolution,
  type TwoPointTerm,
} from "@/lib/tool-kit/chem-physical/arrhenius";
import { MOLAR_ENERGY_UNITS } from "@/lib/tool-kit/chem-physical/gibbs";
import { unitFactor, unitSymbol } from "@/lib/tool-kit/chem-physical/heat";
import { temperatureSymbol } from "@/lib/tool-kit/chem-physical/units";
import {
  fewestSigFigs,
  formatPlain,
  formatSig,
} from "@/lib/tool-kit/chemistry/format";

/* ------------------------------------------------------ two points */

function TwoPointAnswer({
  solution,
  texts,
  units,
  kUnit,
  sig,
  strings,
}: {
  solution: TwoPointSolution;
  texts: { k1: string; t1: string; k2: string; t2: string; ea: string };
  units: { ea: string; t: string };
  kUnit: string;
  sig: number;
  strings: ArrheniusStrings;
}) {
  const fmt = useToolNumber();
  const { unknown } = solution;
  const p = (value: number) => formatPlain(fmt, value);
  const labels = { ea: strings.termEa, k2: strings.termK2, t2: strings.termT2 };
  const answerText = {
    ea: eaText(fmt, solution.ea, units.ea, sig),
    k2: `${formatSig(fmt, solution.k2, sig)} ${kUnit}`.trim(),
    t2: `${formatSig(fmt, solution.answer, sig)} ${temperatureSymbol(units.t)}`,
  }[unknown];
  const conversions: string[] = [];
  if (units.t !== "K") {
    for (const key of ["t1", "t2"] as const) {
      if (key !== unknown) {
        conversions.push(
          t(strings.convertLine, {
            label: key === "t1" ? strings.termT1 : strings.termT2,
            value: texts[key].trim(),
            unit: temperatureSymbol(units.t),
            converted: `${p(solution[key])} K`,
          })
        );
      }
    }
  }
  if (unknown !== "ea" && unitFactor(MOLAR_ENERGY_UNITS, units.ea) !== 1) {
    conversions.push(
      t(strings.convertLine, {
        label: strings.termEa,
        value: texts.ea.trim(),
        unit: unitSymbol(MOLAR_ENERGY_UNITS, units.ea),
        converted: `${p(solution.ea)} J/mol`,
      })
    );
  }
  const substituted = t(
    {
      ea: strings.twoSubstitutedEa,
      k2: strings.twoSubstitutedK2,
      t2: strings.twoSubstitutedT2,
    }[unknown],
    {
      k1: p(solution.k1),
      k2: p(solution.k2),
      t1: p(solution.t1),
      t2: p(solution.t2),
      ea: p(solution.ea),
      r: R_TEXT,
      result: {
        ea: `${formatSig(fmt, solution.ea, sig + 1)} J/mol`,
        k2: formatSig(fmt, solution.k2, sig + 1),
        t2: `${formatSig(fmt, solution.t2, sig + 1)} K`,
      }[unknown],
    }
  );
  const rearranged = {
    ea: strings.twoRearrangedEa,
    k2: strings.twoRearrangedK2,
    t2: strings.twoRearrangedT2,
  }[unknown];
  const needsConversion =
    (unknown === "ea" && unitFactor(MOLAR_ENERGY_UNITS, units.ea) !== 1) ||
    (unknown === "t2" && units.t !== "K");
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={labels[unknown]} value={answerText} />
        <StatCard
          label={strings.lnRatio}
          value={formatSig(fmt, Math.log(solution.k2 / solution.k1), sig)}
        />
        <StatCard
          label={strings.termA}
          value={`${formatSig(fmt, solution.a, sig)} ${kUnit}`.trim()}
        />
      </StatGrid>
      {solution.ea < 0 ? <Notice>{strings.negativeEaNote}</Notice> : null}
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>{strings.twoEquation}</p>
        <p>{rearranged}</p>
        {conversions.map((line) => (
          <p key={line}>{line}</p>
        ))}
        <p>{substituted}</p>
        {needsConversion ? (
          <p>
            {t(strings.answerLine, {
              result:
                unknown === "ea"
                  ? `${formatSig(fmt, solution.ea, sig + 1)} J/mol`
                  : `${formatSig(fmt, solution.t2, sig + 1)} K`,
              answer: answerText,
            })}
          </p>
        ) : null}
        <p>
          {t(strings.preExponentialLine, {
            a: formatSig(fmt, solution.a, sig + 1),
          })}
        </p>
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyText, {
          label: labels[unknown],
          value: answerText,
        })}
      />
    </>
  );
}

export function TwoPointPanel({
  strings,
  sigChoice,
}: {
  strings: ArrheniusStrings;
  sigChoice: string;
}) {
  const [unknown, setUnknown] = useState<TwoPointTerm>("ea");
  const [text, setText] = useState({
    k1: "1.66",
    t1: "650.",
    k2: "7.39",
    t2: "700.",
    ea: "",
  });
  const [unit, setUnit] = useState({ ea: "kJ", t: "K" });
  const [kUnit, setKUnit] = useState("L mol⁻¹ s⁻¹");
  const outcome = solveTwoPoint({ unknown, text, unit });
  const keys = ["k1", "t1", "k2", "t2", "ea"] as const;
  const typed = keys.filter((key) => key !== unknown).map((key) => text[key]);
  const sig =
    sigChoice === "match"
      ? fewestSigFigs(typed, DEFAULT_SIG)
      : Number(sigChoice);
  const labels: Record<string, string> = {
    k1: strings.termK1,
    t1: strings.termT1,
    k2: strings.termK2,
    t2: strings.termT2,
    ea: strings.termEa,
  };
  const label = (key: keyof typeof text) =>
    unknown === key
      ? t(strings.answerSuffix, { label: labels[key] })
      : labels[key];
  const rate = (key: "k1" | "k2") =>
    unknown === key ? (
      <SolvedCell
        id={`arr2-${key}`}
        key={key}
        label={label(key)}
        text={strings.solvedCell}
      />
    ) : (
      <TextField
        id={`arr2-${key}`}
        key={key}
        label={label(key)}
        numeric
        onChange={(value) => setText({ ...text, [key]: value })}
        value={text[key]}
      />
    );
  const temperature = (key: "t1" | "t2") => (
    <QuantityInput
      id={`arr2-${key}`}
      key={key}
      label={label(key)}
      onUnitChange={(value) => setUnit({ ...unit, t: value })}
      onValueChange={(value) => setText({ ...text, [key]: value })}
      solved={unknown === key}
      unit={unit.t}
      unitLabel={strings.unitTemperature}
      units={TEMPERATURE_OPTIONS}
      value={text[key]}
    />
  );
  return (
    <>
      <PillGroup
        label={strings.solveLabel}
        name="arr2-solve"
        onChange={setUnknown}
        options={[
          { value: "ea", label: strings.termEa },
          { value: "k2", label: strings.termK2 },
          { value: "t2", label: strings.termT2 },
        ]}
        value={unknown}
      />
      <div className="flex flex-wrap items-center gap-2">
        <span className={LABEL}>{strings.tryLabel}</span>
        <button
          className={BUTTON_SMALL}
          onClick={() => {
            setUnknown("ea");
            setUnit({ ea: "kJ", t: "K" });
            setText({ k1: "1.66", t1: "650.", k2: "7.39", t2: "700.", ea: "" });
            setKUnit("L mol⁻¹ s⁻¹");
          }}
          type="button"
        >
          {strings.exampleN2O5}
        </button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {rate("k1")}
        {temperature("t1")}
        {rate("k2")}
        {temperature("t2")}
        <QuantityInput
          id="arr2-ea"
          label={label("ea")}
          onUnitChange={(value) => setUnit({ ...unit, ea: value })}
          onValueChange={(value) => setText({ ...text, ea: value })}
          solved={unknown === "ea"}
          unit={unit.ea}
          unitLabel={strings.unitEa}
          units={ENERGY_OPTIONS}
          value={text.ea}
        />
        <TextField
          hint={strings.kUnitHint}
          id="arr2-kunit"
          label={strings.kUnit}
          onChange={setKUnit}
          value={kUnit}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {outcome.ok ? (
          <TwoPointAnswer
            kUnit={kUnit.trim()}
            sig={sig}
            solution={outcome}
            strings={strings}
            texts={text}
            units={unit}
          />
        ) : (
          <FailureNotice
            failure={outcome}
            labels={labels}
            messages={arrheniusMessages(strings)}
          />
        )}
      </section>
    </>
  );
}
